import { createButton } from "../utils/dom.js";

const COMPACT_CONTROLS_MAX_WIDTH = 480;

/**
 * Builds and maintains the viewer controls and compact overflow menu.
 */
export default class Controls {
  elements = {};

  groups = {};

  #resizeObserver;

  #buttonListenerController;

  constructor({
    bookContainer,
    bookLayout,
    bookWrapper,
    splideRoot,
    i18n,
    hasToc,
    thumbnails,
    tocTarget,
    thumbnailsTarget,
    actions,
  }) {
    this.bookContainer = bookContainer;
    this.i18n = i18n;

    this.root = bookLayout.querySelector(":scope > .rhea-controls-placeholder");
    if (this.root) {
      this.root.replaceChildren();
      this.root.classList.remove("rhea-controls-placeholder");
      this.root.removeAttribute("aria-hidden");
    } else {
      this.root = document.createElement("div");
    }
    this.root.classList.add("rhea-controls");
    this.#buttonListenerController = new (
      this.root.ownerDocument.defaultView?.AbortController ??
      globalThis.AbortController
    )();

    this.#buildElements({
      splideRoot,
      i18n,
      hasToc,
      thumbnails,
      tocTarget,
      thumbnailsTarget,
      actions,
    });
    this.#buildGroups({ hasToc, thumbnails });
    this.#buildOverflow(`${splideRoot.id}-controls-overflow`);

    if (!this.root.isConnected) bookWrapper.after(this.root);

    this.root.addEventListener("keydown", this.#handleKeydown);
    if ("ResizeObserver" in window) {
      this.#resizeObserver = new window.ResizeObserver(this.syncLayout);
      this.#resizeObserver.observe(bookContainer);
    }
    this.syncLayout();
  }

  #buildElements({
    splideRoot,
    i18n,
    hasToc,
    thumbnails,
    tocTarget,
    thumbnailsTarget,
    actions,
  }) {
    const button = (text, callback, cssClass, aria) =>
      createButton(text, callback, cssClass, aria, this.root.ownerDocument, {
        signal: this.#buttonListenerController.signal,
      });
    if (hasToc) {
      this.elements.toc = button(
        i18n.toc,
        actions.toggleToc,
        ["rhea-icon", "toc"],
        { controls: tocTarget.id, expanded: "false" },
      );
    }
    if (thumbnails) {
      this.elements.thumbnails = button(
        i18n.thumbnails,
        actions.toggleThumbnails,
        ["rhea-icon", "thumbnails"],
        { controls: thumbnailsTarget.id, expanded: "false" },
      );
    }

    const trackAria = { controls: actions.trackId };
    this.elements.first = button(
      i18n.first,
      actions.first,
      ["rhea-icon", "first"],
      trackAria,
    );
    this.elements.prev = button(
      i18n.prev,
      actions.previous,
      ["rhea-icon", "previous"],
      trackAria,
    );
    this.elements.next = button(
      i18n.next,
      actions.next,
      ["rhea-icon", "next"],
      trackAria,
    );
    this.elements.last = button(
      i18n.last,
      actions.last,
      ["rhea-icon", "last"],
      trackAria,
    );
    this.elements.firstOverflow = button(
      i18n.first,
      actions.first,
      ["rhea-icon", "first", "rhea-boundary-navigation"],
      trackAria,
    );
    this.elements.lastOverflow = button(
      i18n.last,
      actions.last,
      ["rhea-icon", "last", "rhea-boundary-navigation"],
      trackAria,
    );

    const bookAria = { controls: splideRoot.id };
    this.elements.zoomIn = button(
      i18n.zoomIn,
      actions.zoomIn,
      ["rhea-icon", "zoomPlus"],
      bookAria,
    );
    this.elements.zoomOut = button(
      i18n.zoomOut,
      actions.zoomOut,
      ["rhea-icon", "zoomMinus"],
      bookAria,
    );
    this.elements.zoomOut.disabled = true;
    this.elements.changeDisplay = button(
      i18n.changeDisplay,
      actions.changeDisplay,
      ["rhea-icon", "toggle", "mobile"],
      bookAria,
    );
    this.elements.expand = button(
      i18n.expand,
      actions.expand,
      ["rhea-icon", "expand"],
      bookAria,
    );
    this.elements.compress = button(
      i18n.compress,
      actions.compress,
      ["rhea-icon", "compress"],
      bookAria,
    );
    this.elements.downloadPages = button(
      i18n.downloadPages,
      actions.downloadPages,
      ["rhea-icon", "downloadPage"],
    );
    this.elements.controlsMenu = button(
      i18n.moreControls,
      () => this.toggleMenu(),
      ["rhea-icon", "controlsMenu"],
      { expanded: "false" },
    );
  }

  #buildGroups({ hasToc, thumbnails }) {
    const group = (name, ...elements) => {
      const item = document.createElement("li");
      item.classList.add("rhea-controls-group", `rhea-controls-group-${name}`);
      item.append(...elements.filter(Boolean));
      return item;
    };

    this.groups.sidebar = group(
      "sidebar",
      hasToc && this.elements.toc,
      thumbnails && this.elements.thumbnails,
    );
    this.groups.boundaryNavigation = group(
      "boundary-navigation",
      this.elements.firstOverflow,
      this.elements.lastOverflow,
    );
    this.groups.arrows = group(
      "arrows",
      this.elements.first,
      this.elements.prev,
      this.elements.next,
      this.elements.last,
    );
    this.groups.zoom = group(
      "zoom",
      this.elements.zoomIn,
      this.elements.zoomOut,
    );
    this.groups.download = group("download", this.elements.downloadPages);
    this.groups.pageDisplay = group(
      "page-display",
      this.elements.changeDisplay,
    );
    this.groups.fullscreen = group(
      "fullscreen",
      this.elements.expand,
      this.elements.compress,
    );
    this.groups.controlsMenu = group(
      "controls-menu",
      this.elements.controlsMenu,
    );
  }

  #buildOverflow(id) {
    this.overflow = document.createElement("div");
    this.overflow.classList.add("rhea-controls-overflow");
    this.overflow.id = id;
    this.elements.controlsMenu.setAttribute("aria-controls", id);

    this.overflowPanel = document.createElement("div");
    this.overflowPanel.classList.add("rhea-controls-overflow-panel");
    this.overflowList = document.createElement("ul");
    this.overflowList.classList.add(
      "rhea-controls-list",
      "rhea-controls-list-overflow",
    );
    this.overflowList.append(
      this.groups.sidebar,
      this.groups.boundaryNavigation,
      this.groups.zoom,
      this.groups.download,
      this.groups.pageDisplay,
    );
    this.overflowPanel.append(this.overflowList);
    this.overflow.append(this.overflowPanel);

    this.primaryList = document.createElement("ul");
    this.primaryList.classList.add(
      "rhea-controls-list",
      "rhea-controls-list-primary",
    );
    this.primaryList.append(
      this.groups.arrows,
      this.groups.fullscreen,
      this.groups.controlsMenu,
    );
    this.root.append(this.overflow, this.primaryList);
  }

  toggleMenu(force) {
    const open =
      force === undefined
        ? !this.root.classList.contains("rhea-controls-menu-open")
        : Boolean(force);
    return this.#setMenuOpen(open);
  }

  syncLayout = () => {
    this.#setMenuOpen(this.root.classList.contains("rhea-controls-menu-open"));
  };

  setPageDisplayVisible(visible) {
    const display = visible ? "" : "none";
    this.elements.changeDisplay.style.display = display;
    this.groups.pageDisplay.style.display = display;
  }

  setPageDisplayMode(useDualPage) {
    this.elements.changeDisplay.classList.toggle("desktop", !useDualPage);
    this.elements.changeDisplay.classList.toggle("mobile", useDualPage);
  }

  setNavigationState(state) {
    this.elements.first.disabled = !state.canGoPrevious;
    this.elements.firstOverflow.disabled = !state.canGoPrevious;
    this.elements.prev.disabled = !state.canGoPrevious;
    this.elements.next.disabled = !state.canGoNext;
    this.elements.last.disabled = !state.canGoNext;
    this.elements.lastOverflow.disabled = !state.canGoNext;
  }

  setZoomInEnabled(enabled) {
    this.elements.zoomIn.disabled = !enabled;
  }

  setZoomOutEnabled(enabled) {
    this.elements.zoomOut.disabled = !enabled;
  }

  destroy() {
    this.#buttonListenerController.abort();
    this.root.removeEventListener("keydown", this.#handleKeydown);
    this.#resizeObserver?.disconnect();
  }

  #setMenuOpen(open) {
    const compact = this.#isCompact();
    const menuOpen = compact && open;
    this.root.classList.toggle("rhea-controls-menu-open", menuOpen);
    this.overflow.toggleAttribute("inert", compact && !menuOpen);
    this.overflow.setAttribute("aria-hidden", String(compact && !menuOpen));
    this.elements.controlsMenu.setAttribute("aria-expanded", String(menuOpen));
    this.elements.controlsMenu.classList.toggle("open", menuOpen);

    const label = menuOpen ? this.i18n.closeControls : this.i18n.moreControls;
    this.elements.controlsMenu.title = label;
    this.elements.controlsMenu.querySelector(".rhea-button__text").textContent =
      label;
    return menuOpen;
  }

  #isCompact() {
    const width = this.bookContainer.getBoundingClientRect().width;
    return (width || window.innerWidth) <= COMPACT_CONTROLS_MAX_WIDTH;
  }

  #handleKeydown = (event) => {
    if (
      event.key !== "Escape" ||
      !this.root.classList.contains("rhea-controls-menu-open")
    ) {
      return;
    }

    event.preventDefault();
    this.#setMenuOpen(false);
    this.elements.controlsMenu.focus();
  };
}
